function normaliseIdentifier(value) {
  return String(value || "").replace(/[\s-]/g, "");
}

function validateABN(value) {
  const abnValue = normaliseIdentifier(value);
  if (!/^\d{11}$/.test(abnValue)) {
    return "ABN must be 11 digits";
  }

  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const digits = abnValue.split("").map(Number);
  digits[0] -= 1;
  const sum = digits.reduce(
    (total, digit, index) => total + digit * weights[index],
    0
  );

  return sum % 89 === 0 ? null : "Invalid ABN";
}

function validateACN(value) {
  const acnValue = normaliseIdentifier(value);
  if (!/^\d{9}$/.test(acnValue)) {
    return "ACN must be 9 digits";
  }

  const weights = [8, 7, 6, 5, 4, 3, 2, 1];
  const digits = acnValue.split("").map(Number);
  const sum = weights.reduce(
    (total, weight, index) => total + digits[index] * weight,
    0
  );
  const checkDigit = (10 - (sum % 10)) % 10;

  return checkDigit === digits[8] ? null : "Invalid ACN";
}

/*
 * ATO weighted modulus-11 TFN checksums. TFNs are issued in 8- and 9-digit forms and
 * the two use DIFFERENT weight sets — the 8-digit set is not the 9-digit set with its
 * trailing weight dropped — so the arrays are kept separate and picked by length.
 * Mirrored on the server in helper/redactTfn.js; the two copies are deliberate and
 * must be changed together.
 *
 * Unlike validateABN/validateACN, an empty value returns null rather than a message:
 * a missing TFN is not an error here, because required-ness is the form's job. This
 * matches validateTfn in helper/redactTfn.js.
 */
const TFN_WEIGHTS_9 = [1, 4, 3, 7, 5, 8, 6, 9, 10];
const TFN_WEIGHTS_8 = [10, 7, 8, 4, 6, 3, 5, 1];

function validateTfn(value) {
  // Deliberately does not reuse normaliseIdentifier above: that helper collapses
  // any falsy input via `value || ""`, so it reads a numeric 0 or a false as
  // absent where the server reads them as present-but-malformed. Reusing it made
  // the two mirrored copies disagree — see test/tfn-parity.js.
  if (value === null || value === undefined) return null;

  // A number cannot carry a leading zero, so an 8-digit numeric value is
  // ambiguous: either a genuine 8-digit TFN, or a 9-digit one whose leading zero
  // was lost to JSON/number coercion. Both pass their own checksum — 010000177
  // truncates to 10000177, which is itself valid — so accepting one risks writing
  // a different taxpayer's number downstream. The old 9-digit-only rule caught
  // this truncation for free; widening to 8 digits removed that safety net.
  if (typeof value === "number") {
    return "TFN must be supplied as text so a leading zero is preserved";
  }

  const raw = String(value);
  const tfn = raw.replace(/[\s-]/g, "");
  // An empty value is absent, and required-ness is the form's job. A value that
  // is present but collapses to nothing once separators are stripped ("   ", "-")
  // is a filled field holding no TFN, which is malformed rather than absent.
  if (!tfn) return raw === "" ? null : "TFN must be 8 or 9 digits";
  if (!/^\d{8,9}$/.test(tfn)) return "TFN must be 8 or 9 digits";

  const weights = tfn.length === 8 ? TFN_WEIGHTS_8 : TFN_WEIGHTS_9;
  const sum = tfn
    .split("")
    .reduce((total, digit, index) => total + Number(digit) * weights[index], 0);
  return sum % 11 === 0 ? null : "Invalid TFN";
}

/*
 * tfnQuestionNamesFromSchema()
 * Which questions in a SurveyJS form schema hold a TFN.
 *
 * Read from the SCHEMA rather than the answers because the `tfn: true` flag
 * lives on the question definition — a firm may ask for a tax file number under
 * any name it likes and flag it instead of naming it `tfn`, and the answers
 * alone cannot tell a TFN from any other short string.
 *
 * Mirrors auditTfnQuestionsInForm (helper/clientOnboarding.js), the audit the
 * server validates and redacts by, walking the same containers so a TFN asked
 * inside a matrix or a dynamic panel is found. It is mirrored rather than
 * imported for the same reason validateTfn below is: CRA's module scope stops at
 * client/src, so view code cannot reach into helper/. test/tfn-parity.js asserts
 * the two agree — two notions of "this is a TFN question" is how an answer ends
 * up validated on one tier and waved through on the other.
 */
const TFN_QUESTION_CONTAINERS = [
  "pages",
  "elements",
  "templateElements",
  "columns",
  "rows",
  "choices",
];

function tfnQuestionNamesFromSchema(schema) {
  const names = [];

  const visit = (node) => {
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (!node || typeof node !== "object") return;

    const name = node.name || node.value;
    const isTfnQuestion =
      node.tfn === true || (typeof name === "string" && /(^|_)tfn$/i.test(name));
    if (isTfnQuestion && name) names.push(name);

    TFN_QUESTION_CONTAINERS.forEach((key) => {
      if (Array.isArray(node[key])) visit(node[key]);
    });
  };

  visit(schema && typeof schema === "object" ? schema : {});
  return names.filter((name) => typeof name === "string" && name.trim() !== "");
}

/*
 * The one verdict a submitting client cannot act on, so both tiers have to tell
 * it apart from the rest. Mirrors TFN_NUMERIC_INPUT_MESSAGE in helper/redactTfn.js.
 */
const TFN_NUMERIC_INPUT_MESSAGE =
  "TFN must be supplied as text so a leading zero is preserved";

/*
 * isTfnLeafKey()
 * Whether an answer key holds a TFN: the /(^|_)tfn$/i naming rule, or one of
 * the names the schema flags. Mirrors isTfnLeafKey in helper/redactTfn.js, the
 * rule the server validates and redacts by; test/tfn-parity.js asserts the two
 * agree.
 */
function isTfnLeafKey(key, fieldNames = []) {
  return /(^|_)tfn$/i.test(key) || fieldNames.includes(key);
}

// The server's leaf predicate (collectTfnValidationErrors): a TFN key whose
// value is a scalar that is not empty once trimmed. Anything else is either an
// unanswered question or a container whose own questions are judged one by one.
function isTfnLeaf(key, value, fieldNames) {
  return (
    isTfnLeafKey(key, fieldNames) &&
    value !== null &&
    value !== undefined &&
    typeof value !== "object" &&
    String(value).trim() !== ""
  );
}

/*
 * collectTfnSubmitErrors()
 * Every TFN failure in an answer set that a submit should be blocked on, each
 * with the question PATH that holds it — never the value.
 *
 * Mirrors collectTfnValidationErrors + classifyTfnSubmissionErrors
 * (helper/redactTfn.js), the pair the Tranche 2 intake route rejects by, and
 * mirrored for the same reason validateTfn is: CRA's module scope stops at
 * client/src, so view code cannot reach into helper/. test/tfn-parity.js asserts
 * the two agree.
 *
 * It WALKS, and that is the point. A firm that asks for tax file numbers in a
 * matrix or a dynamic panel holds them at `tfn_details[i].tfn`, so the flagged
 * question is a row column and the container's own name is not a TFN question at
 * all. A browser check keyed on the container name sees an object, has nothing
 * to validate, and lets the submit through — while the route, which walks,
 * rejects it. The client then sees "Invalid TFN details" with no way to know
 * which row is wrong, on a public link with no staff member to ask.
 *
 * Two exclusions, both matching the route:
 *   - a leaf that trims to nothing is an UNANSWERED question (required-ness is
 *     the form's job), not a malformed one;
 *   - a numeric answer is the FIRM's bug. validateTfn refuses a number outright,
 *     and a question typed `number` in the form editor can never return anything
 *     else, so blocking on it would dead-end every one of that firm's clients on
 *     an error only the firm can clear. The route raises a form-configuration
 *     audit event and lets the submission through.
 *
 * data               - the submitted answers (object)
 * options.fieldNames - the TFN question names from the schema, as on the server
 * returns            - [{ label, message }]
 */
function collectTfnSubmitErrors(data, { fieldNames = [] } = {}) {
  const errors = [];

  const walk = (node, labels) => {
    if (Array.isArray(node)) {
      node.forEach((item, index) => walk(item, labels.concat(`#${index + 1}`)));
      return;
    }
    if (!node || typeof node !== "object") return;

    Object.keys(node).forEach((key) => {
      const value = node[key];
      if (!isTfnLeaf(key, value, fieldNames)) {
        walk(value, labels.concat(key));
        return;
      }

      const message = validateTfn(value);
      if (message && message !== TFN_NUMERIC_INPUT_MESSAGE) {
        errors.push({ label: labels.concat(key).join(" / "), message });
      }
    });
  };

  walk(data, []);
  return errors;
}

/*
 * tfnSubmitError()
 * The TFN message a submit should be blocked on for ONE answer, or null to let
 * it through. The scalar shorthand for collectTfnSubmitErrors above, expressed
 * through it rather than beside it so the two cannot drift: the server's own
 * verdict is classifyTfnSubmissionErrors({ tfn: value }).clientErrors, which is
 * this exact expression (see test/tfn-parity.js).
 */
function tfnSubmitError(value) {
  const errors = collectTfnSubmitErrors({ tfn: value });
  return errors.length > 0 ? errors[0].message : null;
}

/*
 * tfnFieldError()
 * The TFN message for ONE field on a Karbon onboarding form, or null to let it
 * through. Mirrors the Karbon submit gate (controller/formController.js), which
 * blocks on collectTfnValidationErrors (helper/redactTfn.js) with no classify
 * step. So, unlike tfnSubmitError above, a numeric answer blocks here, as it
 * does there. test/tfn-parity.js asserts the two agree.
 *
 * name       - the question name
 * value      - the question's answer
 * fieldNames - the TFN question names from the schema (tfnQuestionNamesFromSchema)
 * returns    - the message, or null
 */
function tfnFieldError(name, value, fieldNames = []) {
  return isTfnLeaf(name, value, fieldNames) ? validateTfn(value) : null;
}

module.exports = {
  normaliseIdentifier,
  validateABN,
  validateACN,
  validateTfn,
  tfnQuestionNamesFromSchema,
  isTfnLeafKey,
  tfnSubmitError,
  collectTfnSubmitErrors,
  tfnFieldError,
};
